/**
 * envelope — the client-side reader for the canonical API envelope
 * (RES-ENV-001), in BOTH directions.
 *
 *   ERROR   { success: false, error: { code, message, details? }, requestId? }
 *   SUCCESS { success: true,  data: <payload> }
 *
 * This lives in `@puffin-core/api-client` because that is the ecosystem's HTTP
 * boundary: it is browser-safe, has no `@puffin-core` dependencies, and is consumed by
 * both the axios path (`normalizeError`) and the one hand-rolled `fetch` client
 * that cannot adopt axios (`port-web`'s `platformApi.js`, which needs blob
 * export). One reader, one place.
 *
 * Every non-canonical ERROR body — a network failure with no response, a
 * proxy's HTML 502, an opaque gateway JSON — degrades to nulls. A client that
 * throws while trying to read an error is worse than the error it was reading.
 *
 * ## 🔴 The SUCCESS half, and why it behaves the OPPOSITE way
 *
 * This module owned only the error direction until 2026-08-19, so every
 * consumer invented its own success reader. Censused in planner-web's sales
 * feature: **fourteen sites, NINE different answers** — `result?.data || {}`,
 * `|| []`, `|| result || {}`, `|| result?.holidays || []`,
 * `|| result?.locations || []`, `|| null`, `|| EMPTY_VERSIONS`, and so on.
 *
 * They fail identically: an unrecognised body silently becomes an empty list,
 * and an empty list renders as *"this tenant has no holidays"* — a confident
 * zero indistinguishable from the truth. That is precisely the failure class
 * PLN-SP-UAT-004 was called in to fix at the QUERY layer (a group-scoped read
 * returning valid SQL over an empty set); the READ layer had it too.
 *
 * So `readEnvelopeData` **throws** on a shape it does not recognise. Every
 * consumer already has a `.catch` and an honest error state; throwing routes an
 * unreadable body there, and the surface says "we could not read this" instead
 * of "there is nothing here". Degrading is right for an error and wrong for a
 * payload — the asymmetry is the whole point, not an inconsistency.
 *
 * @module @puffin-core/api-client/envelope
 */

/**
 * Thrown when a body does not match the canonical success envelope.
 *
 * Carries the body so a logger can attach it, and names what it saw so the
 * failure is diagnosable without opening the network tab.
 */
export class EnvelopeShapeError extends Error {
  /**
   * @param {string} message
   * @param {*} body - the offending body.
   */
  constructor(message, body) {
    super(message);
    this.name = 'EnvelopeShapeError';
    this.body = body;
  }
}

/**
 * Does `body` match the canonical SUCCESS envelope?
 *
 * Requires `success === true` AND the `data` key to be PRESENT. Present, not
 * truthy: `null`, `0`, `''` and `false` are all real payloads a server may
 * deliberately send, and treating them as absent is how `|| []` turned "zero
 * sales" into "no data". An ABSENT `data` key means the body is not this
 * contract, and guessing at that point is the habit this reader retires.
 *
 * @param {*} body - a parsed response body
 * @returns {boolean}
 */
export function isEnvelopeSuccess(body) {
  return (
    typeof body === 'object' &&
    body !== null &&
    !Array.isArray(body) &&
    body.success === true &&
    'data' in body
  );
}

/**
 * Describe a body for the error message: its keys, or its type when it has none.
 *
 * @param {*} body
 * @returns {string}
 */
function describeBody(body) {
  if (typeof body !== 'object' || body === null) return `${typeof body}`;
  if (Array.isArray(body)) return `array(${body.length})`;
  const keys = Object.keys(body);
  return keys.length > 0 ? `object with keys [${keys.join(', ')}]` : 'empty object';
}

/**
 * The payload of a canonical success envelope.
 *
 * @param {*} body - a parsed response body
 * @returns {*} `body.data`, verbatim — including `null`, `0`, `''` and `false`.
 * @throws {EnvelopeShapeError} when `body` is not a canonical success envelope.
 */
export function readEnvelopeData(body) {
  if (!isEnvelopeSuccess(body)) {
    throw new EnvelopeShapeError(
      `Expected the RES-ENV-001 success envelope { success: true, data } but received ${describeBody(body)}.`,
      body,
    );
  }
  return body.data;
}

/**
 * The payload of a LIST route, which is the array itself (`sendSuccess(res, rows)`).
 *
 * The step after `readEnvelopeData` (or after a facade verb, which has already opened the
 * envelope). Where the envelope reader retired `result?.data || []`, this retires the `|| []` that
 * remained once the payload was in hand: a list that could not be read rendered as "nothing to
 * review" — "no timecards waiting", "no open violations" — a confident empty answer made from a body
 * nobody read (UAT-BLOCKERS-2026-09-18). It throws instead, so the caller's error state runs.
 *
 * An empty array is returned as the truth it is; only a non-array throws.
 *
 * @param {*} payload - an already-unwrapped payload
 * @param {string} subject - what the list is ("actionable attestations"), for the error message —
 *   which reaches logs and developers, never the reader
 * @returns {Array} `payload`, the same array
 * @throws {EnvelopeShapeError} when `payload` is not an array.
 */
export function readListPayload(payload, subject) {
  if (!Array.isArray(payload)) {
    throw new EnvelopeShapeError(
      `Expected the ${subject} payload to be a list but received ${describeBody(payload)}.`,
      payload,
    );
  }
  return payload;
}

/**
 * The payload of a RECORD route, which is the object itself (`sendSuccess(res, record)`) — the twin of
 * {@link readListPayload} (UAT-BLOCKERS Q-20, the object half).
 *
 * It retires the `|| {}` left once the payload was in hand: a record that could not be read rendered as an empty one —
 * every field blank, every count absent — a confident answer made from a body nobody read. It throws instead, so the
 * caller's error state runs. An empty object is returned as the truth it is; `null`, a list and a scalar throw. A route
 * that may legitimately answer `null` ("no record yet") does not use this reader: its caller states that case itself.
 *
 * @param {*} payload - an already-unwrapped payload
 * @param {string} subject - what the record is ("store settings"), for the error message — which reaches logs and
 *   developers, never the reader
 * @returns {Object} `payload`, the same object
 * @throws {EnvelopeShapeError} when `payload` is not a plain object.
 */
export function readObjectPayload(payload, subject) {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    throw new EnvelopeShapeError(
      `Expected the ${subject} payload to be a record but received ${describeBody(payload)}.`,
      payload,
    );
  }
  return payload;
}

/**
 * The payload AND the envelope's metadata siblings, as an explicit tuple.
 *
 * ## Why this exists, and why it is a SECOND NAMED ACCESSOR rather than an option
 *
 * `readEnvelopeData` returns `data` and discards `pagination` / `meta`, which is correct for a
 * payload reader and wrong for a list surface that has to page. RES-CLIENT-001 FINDING 15 measured
 * the cost of that gap: pos-web's T7 flip silently dropped pagination on a customer list over a
 * 42,529-row fleet, and **every suite stayed green**, because the fixtures manufactured the
 * metadata themselves.
 *
 * The first answer was `readPayloadWithMeta`, which folded the siblings onto the payload as a
 * NON-ENUMERABLE `_meta`. That worked and was ruled **transitional**, for two reasons worth
 * recording:
 *
 *   1. **It mutates the payload.** A caller that spreads, `.map()`s or `JSON.stringify`s the result
 *      loses `_meta` silently — which is exactly how the pos-web subclasses lose it today.
 *   2. **It is invisible.** A non-enumerable property does not show up in a log, a snapshot, or a
 *      debugger's object preview, so its absence looks identical to its presence.
 *
 * An explicit tuple has neither problem: the metadata is a named field the caller destructures, it
 * survives every ordinary operation, and a caller that forgets it gets a `undefined` they can see.
 *
 * ⚠️ This is NOT a tolerant variant of `readEnvelopeData`. It applies the same test and throws the
 * same `EnvelopeShapeError` on the same bodies — one contract, two named readings of it. The choice
 * between them is made at the call site by NAME, never by a branch inside either.
 *
 * @param {*} body - a parsed response body
 * @returns {{data: *, pagination: Object|null, meta: Object|null}} the payload plus its siblings;
 *   absent siblings are `null`, never `undefined`, so a destructured miss is distinguishable from
 *   a sibling the server genuinely omitted.
 * @throws {EnvelopeShapeError} when `body` is not a canonical success envelope.
 */
export function readEnvelopeWithMeta(body) {
  const data = readEnvelopeData(body);
  return {
    data,
    pagination: body.pagination === undefined ? null : body.pagination,
    meta: body.meta === undefined ? null : body.meta,
  };
}

/**
 * Does `body` match the canonical error envelope?
 *
 * Requires `success === false` AND a plain-object `error`. An array `error` is
 * rejected: arrays are objects, but they are not the envelope.
 *
 * @param {*} body - a parsed response body
 * @returns {boolean}
 */
export function isEnvelopeError(body) {
  return (
    typeof body === 'object' &&
    body !== null &&
    body.success === false &&
    typeof body.error === 'object' &&
    body.error !== null &&
    !Array.isArray(body.error)
  );
}

/**
 * Pull `{ message, errorCode, errorDetails }` out of a canonical error body.
 *
 * The `errorCode` / `errorDetails` names are deliberate: `code` on a normalized
 * error already means the AXIOS TRANSPORT code (`ERR_NETWORK`, `ERR_CANCELED`),
 * which ~262 sites across the web apps read. The server's business code needs
 * its own name, and `port-web`'s `platformApi.js` had already chosen these two.
 *
 * @param {*} body - a parsed response body
 * @returns {{ message: string|null, errorCode: string|null, errorDetails: *|null }}
 */
export function unwrapEnvelope(body) {
  if (!isEnvelopeError(body)) {
    return { message: null, errorCode: null, errorDetails: null };
  }
  const { code, message, details } = body.error;
  return {
    message: message ?? null,
    errorCode: code ?? null,
    errorDetails: details ?? null,
  };
}
